import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "./DashboardLayout";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Wand2, Mic2, Copy, Check, ArrowRight, Zap, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";
import { Waveform } from "@/components/landing/Waveform";

const SAMPLE = `বাংলাদেশের অর্থনীতি ২০২৬ সালে নতুন উচ্চতায় পৌঁছেছে। গত মাসে রপ্তানি আয় বেড়েছে ১২ শতাংশ, যা দেশের ইতিহাসে রেকর্ড।`;

function ArticleTypeSelect({ onSelect }) {
    return (
        <div>
            <PageHeader
                eyebrow="New article"
                title="Choose your format."
                subtitle="Create a single-voice article or a multi-voice podcast with different speakers per segment."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
                <button
                    onClick={() => onSelect("single")}
                    className="group text-left bg-white rounded-3xl border border-cream-300 shadow-soft hover:shadow-soft-lg hover:border-coral/40 transition-all p-7"
                >
                    <div className="w-12 h-12 rounded-2xl bg-coral/10 flex items-center justify-center mb-5 group-hover:bg-coral/20 transition">
                        <Mic2 size={20} className="text-coral" />
                    </div>
                    <h3 className="font-display text-[22px] font-semibold tracking-tight text-ink">Single voice</h3>
                    <p className="text-[14px] text-ink-muted mt-2">One voice reads the entire article. Best for news, editorials, and standard content.</p>
                    <p className="mt-5 text-[13px] font-semibold text-coral group-hover:underline inline-flex items-center gap-1">
                        Create → <ArrowRight size={12} />
                    </p>
                </button>

                <button
                    onClick={() => onSelect("multi")}
                    className="group text-left bg-ink text-cream-100 rounded-3xl shadow-soft hover:shadow-soft-lg transition-all p-7 relative overflow-hidden"
                >
                    <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: "radial-gradient(circle at 80% 20%, #FF5B3E 0%, transparent 60%)"}}/>
                    <div className="relative">
                        <div className="w-12 h-12 rounded-2xl bg-coral flex items-center justify-center mb-5">
                            <Users size={20} className="text-white" />
                        </div>
                        <h3 className="font-display text-[22px] font-semibold tracking-tight">Multiple voices</h3>
                        <p className="text-[14px] text-cream-100/70 mt-2">Different voices per segment — perfect for podcasts, interviews, and multi-speaker content.</p>
                        <p className="mt-5 text-[13px] font-semibold text-coral inline-flex items-center gap-1 group-hover:underline">
                            Create → <ArrowRight size={12} />
                        </p>
                    </div>
                </button>
            </div>
        </div>
    );
}

export default function ArticleNew() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState(null); // null = choose, "single" = form, "multi" = redirect
    const [title, setTitle] = useState("");
    const [text, setText] = useState(SAMPLE);
    const [voiceId, setVoiceId] = useState("noor");
    const [language, setLanguage] = useState("Bengali");
    const LANGUAGES = ["Bengali", "English", "Arabic (Gulf)", "Spanish", "French", "Hindi"];
    const [tone, setTone] = useState("news");
    const [voices, setVoices] = useState([]);
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(false);
    const [genStage, setGenStage] = useState(0);
    const [articleData, setArticleData] = useState(null);
    const [copied, setCopied] = useState(false);
    const pollRef = useRef(null);

    const handleModeSelect = (m) => {
        if (m === "multi") { navigate("/dashboard/articles/new-multi"); return; }
        setMode("single");
    };

    useEffect(() => {
        api.get("/voices").then(r => setVoices(r.data)).catch(() => {});
        api.get("/billing/credits").then(r => setCredits(r.data)).catch(() => {});
    }, []);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const charCount = text.length;
    const charsNeeded = charCount;
    const charsAvailable = credits ? (credits.monthly_chars_remaining + credits.topup_chars_remaining) : Infinity;
    const insufficientCredits = credits && charsNeeded > charsAvailable;
    const creditsLow = credits && charsAvailable > 0 && charsAvailable < 5000;

    const fmtChars = (n) => n >= 1000 ? `${(n/1000).toFixed(1).replace(/\.0$/,"")}k` : String(n);
    const isFreePlan = (user?.plan || "free") === "free";
    const tones = [
        { id: "news", label: "Standard news" },
        { id: "breaking", label: "Breaking news" },
        { id: "feature", label: "Feature story" },
        { id: "sports", label: "Sports" },
        { id: "calm", label: "Editorial / calm" },
        { id: "motivational", label: "Motivational" },
        { id: "friendly", label: "Friendly" },
        { id: "dramatic", label: "Dramatic" },
        { id: "storytelling", label: "Storytelling" },
        { id: "podcast", label: "Podcast" },
        { id: "formal", label: "Formal / institutional" },
    ];

    const onCopyEmbed = async (aid) => {
        const code = `<script src="https://audo.dev/embed/v1.js" data-article="${aid}"></script>`;
        try { await navigator.clipboard.writeText(code); } catch {}
        setCopied(true);
        toast.success("Embed code copied — paste into your website");
        setTimeout(() => setCopied(false), 2200);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (insufficientCredits) {
            toast.error("Not enough credits. Buy top-up credits in Billing.");
            return;
        }
        setLoading(true);
        setGenStage(1);
        try {
            const { data } = await api.post("/articles", { title, text, voice_id: voiceId, tone, language });
            setArticleData(data);
            setGenStage(2);
            // Refresh credits after submission
            api.get("/billing/credits").then(r => setCredits(r.data)).catch(() => {});
            toast.success("Submitted — audio generating in background");
            pollRef.current = setInterval(async () => {
                try {
                    const { data: upd } = await api.get(`/articles/${data.id}`);
                    if (upd.status !== "processing") {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        setArticleData(upd);
                        setGenStage(3);
                        if (upd.status === "ready") toast.success("Audio ready!");
                    }
                } catch {}
            }, 3000);
        } catch (err) {
            const msg = getErrorMessage(err);
            if (msg.includes("Insufficient credits") || err?.response?.status === 402) {
                toast.error("Not enough credits. Go to Billing to buy top-up credits.", { duration: 6000 });
            } else {
                toast.error(msg);
            }
            setGenStage(0);
        } finally { setLoading(false); }
    };

    if (!mode) return <ArticleTypeSelect onSelect={handleModeSelect} />;

    return (
        <div data-testid="article-new-page">
            <div className="mb-4">
                <button onClick={() => setMode(null)} className="text-[13px] text-ink-muted hover:text-coral transition">← Change format</button>
            </div>
            <PageHeader
                eyebrow="New article · Single voice"
                title="Generate Bengali audio."
                subtitle="Paste your article text. Pick a voice. Done."
            />

            {/* Credit balance bar */}
            {credits && (
                <div className={`mb-5 flex items-center gap-3 px-5 py-3 rounded-2xl text-[13px] ${
                    insufficientCredits ? "bg-red-50 border border-red-200 text-red-700" :
                    creditsLow ? "bg-amber-50 border border-amber-200 text-amber-800" :
                    "bg-mint/10 border border-mint/30 text-ink"
                }`}>
                    {insufficientCredits
                        ? <AlertTriangle size={14} className="flex-shrink-0"/>
                        : creditsLow
                        ? <AlertTriangle size={14} className="flex-shrink-0 text-amber-600"/>
                        : <Zap size={14} className="text-mint flex-shrink-0"/>}
                    <span>
                        <strong>{fmtChars(credits.monthly_chars_remaining)} chars</strong> monthly remaining
                        {credits.topup_chars_remaining > 0 && <> · <strong className="text-mint">{fmtChars(credits.topup_chars_remaining)} top-up chars</strong></>}
                        {" · "}This text is <strong>{charsNeeded.toLocaleString()} chars</strong>
                    </span>
                    {(insufficientCredits || creditsLow) && (
                        <Link to="/dashboard/billing" className="ml-auto text-[12px] font-semibold underline whitespace-nowrap">
                            Buy credits →
                        </Link>
                    )}
                </div>
            )}

            <form onSubmit={submit} data-testid="article-new-form" className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-2">Title</label>
                        <input
                            required value={title} onChange={(e) => setTitle(e.target.value)}
                            data-testid="article-title" placeholder="e.g. বাংলাদেশের অর্থনীতি ২০২৬"
                            className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                        />
                    </div>

                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-2">Article text ({language})</label>
                        <textarea
                            required rows={10} value={text} onChange={(e) => setText(e.target.value)}
                            data-testid="article-text"
                            className="bn w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[15.5px] leading-[1.6] resize-none focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                        />
                        <p className="mt-2 text-[12px] text-ink-muted">{text.length.toLocaleString()} chars used · {credits ? `${fmtChars(charsAvailable)} chars remaining` : ""} · ~{Math.max(1, Math.floor(text.length / 14 / 60))} min audio</p>
                    </div>
                </div>

                <div className="space-y-5">
                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <div className="flex items-center gap-2 mb-4">
                            <svg className="w-[14px] h-[14px] text-coral" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Language</span>
                        </div>
                        <select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                        >
                            {LANGUAGES.map(lang => (
                                <option key={lang} value={lang}>{lang}</option>
                            ))}
                        </select>
                    </div>

                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <div className="flex items-center gap-2 mb-4">
                            <Mic2 size={14} className="text-coral"/>
                            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Voice</span>
                        </div>
                        <select
                            value={voiceId}
                            onChange={(e) => setVoiceId(e.target.value)}
                            className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                        >
                            {voices.map(v => (
                                <option key={v.id} value={v.id}>
                                    {v.name} — {v.gender === "female" ? "F" : "M"} · {v.accent} · {v.tone}
                                </option>
                            ))}
                        </select>
                        {voices.length > 0 && (
                            <p className="text-[11px] text-ink-muted mt-2">
                                {voices.find(v => v.id === voiceId)?.tone || ""}
                                {isFreePlan && <> · <span className="text-coral">Free plan: 2 voices. <Link to="/dashboard/billing" className="underline">Upgrade</Link> for more.</span></>}
                            </p>
                        )}
                    </div>

                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <div className="flex items-center gap-2 mb-4">
                            <Wand2 size={14} className="text-coral"/>
                            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Tone &amp; style</span>
                        </div>
                        <select
                            value={isFreePlan ? "news" : tone}
                            onChange={(e) => setTone(e.target.value)}
                            disabled={isFreePlan}
                            className="w-full bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:bg-cream-200 disabled:text-ink-muted"
                        >
                            {tones.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-ink-muted mt-2">
                            {isFreePlan ? "Free plan uses Standard news tone only. Upgrade to unlock all tones." : "Pro and Business include higher-quality audio rendering."}
                        </p>
                    </div>

                    <button
                        type="submit" disabled={loading || genStage >= 2 || !title || !text || (credits && insufficientCredits)} data-testid="generate-btn"
                        className="w-full bg-ink hover:bg-coral text-cream-100 rounded-full py-4 text-[15px] font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:hover:bg-ink"
                    >
                        {genStage === 1 ? <><Loader2 size={16} className="animate-spin"/> Submitting…</> : <><Wand2 size={15}/> Generate audio</>}
                    </button>
                    {insufficientCredits ? (
                        <p className="text-[12px] text-red-600 text-center font-medium">No credits left. <Link to="/dashboard/billing" className="underline">Buy top-up →</Link></p>
                    ) : (
                        <p className="text-[12px] text-ink-muted text-center">Uses <strong>{charsNeeded.toLocaleString()}</strong> of your {fmtChars(charsAvailable)} available chars</p>
                    )}
                </div>
            </form>

            {genStage >= 2 && articleData && (
                <div data-testid="generation-status" className="fixed bottom-6 right-6 bg-white rounded-3xl shadow-soft-lg border border-cream-300 p-5 w-[340px] z-50">
                    <div className="flex items-center gap-2 mb-3">
                        {genStage === 3
                            ? <span className="w-2 h-2 rounded-full bg-mint"/>
                            : <span className="w-2 h-2 rounded-full bg-coral animate-pulse"/>
                        }
                        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">
                            {genStage === 3 ? (articleData.status === "ready" ? "Audio ready!" : "No audio") : "Generating audio…"}
                        </span>
                    </div>
                    <div className="h-8 mb-3">
                        <Waveform bars={28} color={genStage === 3 ? "#2EE8B5" : "#FF5B3E"} height={28} playing={genStage === 2}/>
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Embed code</p>
                    <div className="relative bg-cream-100 rounded-2xl p-3 pr-10 mb-3">
                        <code className="text-[10px] font-mono text-ink break-all leading-relaxed">
                            {`<script src="https://audo.dev/embed/v1.js" data-article="${articleData.id}"></script>`}
                        </code>
                        <button onClick={() => onCopyEmbed(articleData.id)}
                            className={`absolute top-2 right-2 p-1.5 rounded-lg transition-colors ${copied ? "bg-mint text-ink" : "bg-white hover:bg-coral hover:text-white text-ink-muted"}`}>
                            {copied ? <Check size={12}/> : <Copy size={12}/>}
                        </button>
                    </div>
                    <p className="text-[11px] text-ink-muted mb-3">
                        {genStage === 3
                            ? "Paste the embed code above on your website."
                            : "You can safely leave this page. Audio generates in the background."}
                    </p>
                    <Link to="/dashboard/articles" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-coral hover:underline">
                        Go to My Articles <ArrowRight size={12}/>
                    </Link>
                </div>
            )}
        </div>
    );
}

